import React, { useEffect, useRef, useState } from "react"
import { View } from "react-native"
import { connect } from "react-redux"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { apiRequest } from "@data/redux/actions/api"
import { activitiesService } from "@data/services/activitiesService"
import MarkdownWithLatex from "../../../tutor/home/MarkdownWithLatex"
import FeedbackButton from "@common/components/feedback/FeedbackButton"
import FilledButtonM from "@common/components/button/FilledButtonM"
import { ErrorBanner } from "@common/components/alert/ErrorBanner"
import { hasRole } from "@data/utility/ability"
import { IS_WEB } from "../../utils/mobileUtils"

const GENERATING = "generating"
const READY = "ready"
const FAILED = "failed"

const POLL_INTERVAL_MS = 5000
// Filet de l'écran : passé ce délai, la génération est morte sans que le
// serveur ait pu l'écrire (worker coupé, marqueur resté d'avant l'état
// d'échec) — on propose « Réessayer » au lieu d'attendre.
const POLL_CAP_MS = 180000

// Le serveur expose `correction_status` ; sans lui (serveur pas encore à
// jour), seul le marqueur "..." de correction_markdown dit « en cours ».
const correctionStatusOf = (material) => {
    if (material?.correction_status !== undefined) return material.correction_status
    if (material?.correction_markdown === "...") return GENERATING
    return material?.correction_markdown ? READY : null
}

const MaterialCorrectionDisplay = (props) => {
    const { material, style = {}, user, isParentMode } = props
    const materialId = material?.id
    const [status, setStatus] = useState(correctionStatusOf(material))
    const [correctionContent, setCorrectionContent] = useState(material?.correction_markdown)
    const [keyNotionsContent, setKeyNotionsContent] = useState(material?.key_notions_markdown)
    const [isRetrying, setIsRetrying] = useState(false)
    const [currentFeedback, setCurrentFeedback] = useState(null)
    const [showCorrection, setShowCorrection] = useState(false)

    // Show correction features for tutors and parents
    const isTutor = hasRole(user, "tutor")
    const canViewCorrection = isTutor || isParentMode

    // Set when the screen's cap gives up: a late "generating" answer must not
    // bring the spinner back — only the tutor's « Réessayer » does.
    const gaveUpRef = useRef(false)

    const applyMaterial = (fresh) => {
        const next = correctionStatusOf(fresh)
        if (!(gaveUpRef.current && next === GENERATING)) {
            setStatus(next)
        }
        if (fresh?.correction_markdown) {
            setCorrectionContent(fresh.correction_markdown)
        }
        if (fresh?.key_notions_markdown) {
            setKeyNotionsContent(fresh.key_notions_markdown)
        }
    }

    // The api states are shared between mounted displays: only this material counts.
    const applyResponse = (data) => {
        const fresh = data?.data || data
        if (fresh?.id !== materialId) return false
        applyMaterial(fresh)
        return true
    }

    // Polling response
    useApiStatus(
        activitiesService.getMaterial,
        null,
        false,
        applyResponse,
        () => {
            // On error, continue polling
        },
        true // noIdle - don't clear the API state
    )

    useApiStatus(
        activitiesService.retryMaterialCorrection,
        null,
        true,
        (data) => {
            if (applyResponse(data)) setIsRetrying(false)
        },
        () => setIsRetrying(false)
    )

    useEffect(() => {
        applyMaterial(material)
    }, [material?.correction_markdown, material?.correction_status])

    // Handle feedback loading
    useApiStatus(
        activitiesService.getMaterialFeedback,
        null,
        false,
        (data) => {
            setCurrentFeedback(data?.data || data)
        },
        () => {
            // On error, set feedback to null
            setCurrentFeedback(null)
        },
        true
    )

    // Handle feedback submission
    useApiStatus(
        activitiesService.submitMaterialFeedback,
        null,
        true,
        (data) => {
            setCurrentFeedback(data?.data || data)
        },
        () => {
            // On error, show message handled by useApiStatus
        },
        true
    )

    useEffect(() => {
        if (materialId && correctionContent && hasRole(user, "tutor")) {
            props.apiRequest(activitiesService.getMaterialFeedback, { material_id: materialId, feature_type: 'correction' })
        }
    }, [materialId])

    // Poll while the correction is being generated — until the server says
    // ready or failed, or the screen's own cap gives up on it.
    useEffect(() => {
        if (!materialId || !isTutor || status !== GENERATING) return

        const poll = () => props.apiRequest(activitiesService.getMaterial, { id: materialId })
        poll()
        const interval = setInterval(poll, POLL_INTERVAL_MS)
        const cap = setTimeout(() => {
            gaveUpRef.current = true
            setStatus(FAILED)
        }, POLL_CAP_MS)

        return () => {
            clearInterval(interval)
            clearTimeout(cap)
        }
    }, [materialId, status])

    const handleRetry = () => {
        gaveUpRef.current = false
        setIsRetrying(true)
        props.apiRequest(activitiesService.retryMaterialCorrection, { material_id: materialId })
    }

    const handleFeedback = (feedbackType) => {
        if (!materialId) return

        props.apiRequest(activitiesService.submitMaterialFeedback, {
            material_id: materialId,
            feedback_type: feedbackType,
            feature_type: 'correction',
            comment: null
        })
    }

    // One button, three states. `onPress: null` = disabled.
    const buttonState = {
        [GENERATING]: { label: "Génération de la correction...", loading: true, onPress: null },
        [READY]: {
            label: showCorrection ? "Masquer l'aide" : "Aide à la correction",
            icon: showCorrection ? "eye-slash" : "eye",
            onPress: () => setShowCorrection(!showCorrection),
        },
        [FAILED]: { label: "Réessayer", icon: "redo", loading: isRetrying, onPress: isRetrying ? null : handleRetry },
    }[status]

    // Don't render anything if not tutor/parent or no correction. The parent
    // view neither polls nor retries: it only shows a correction that exists.
    if (!canViewCorrection || !materialId || (!isTutor && status !== READY)) {
        return null
    }

    return (
        <View style={[{ paddingVertical: 10, backgroundColor: IS_WEB ? theme.colors.white : theme.colors.whiteAlmost }, style]}>
            {status === FAILED && (
                <ErrorBanner
                    message="L'aide à la correction n'a pas pu être générée."
                    style={{ marginTop: 0, marginHorizontal: 10 }}
                />
            )}

            {buttonState && (
                <FilledButtonM
                    trackingId={status === FAILED ? "material-correction-retry-button" : "material-correction-display-button"}
                    onPress={() => buttonState.onPress?.()}
                    label={buttonState.label}
                    icon={buttonState.loading ? null : buttonState.icon}
                    loading={buttonState.loading}
                    disabled={!buttonState.onPress}
                    mode="text"
                    color={!buttonState.onPress || (status === READY && showCorrection) ? theme.colors.grey : theme.colors.primary}
                    style={{
                        height: 30,
                        marginBottom: 0,
                    }}
                    contentStyle={{
                        height: 30,
                    }}
                    labelStyle={{ fontSize: 12 }}
                />
            )}

            {/* Show correction content if expanded and available */}
            {showCorrection && status === READY && correctionContent && (() => {
                // Combine the markdown content
                const combinedContent = [
                    keyNotionsContent,
                    correctionContent
                ].filter(Boolean).join('\n\n## Correction \n\n')

                return (
                    <View
                        style={{
                            backgroundColor: theme.colors.whiteAlmost,
                            borderRadius: 8,
                            marginTop: 10,
                        }}
                    >
                        {/* Le bloc suit un énoncé qui prend sa hauteur, dans une vue qui défile :
                            en remplissant ce qu'il reste, le corrigé tombe à zéro sous un énoncé long. */}
                        <MarkdownWithLatex
                            value={combinedContent}
                            autoHeight
                        />
                        
                        {/* Feedback buttons */}
                        <View
                            style={{
                                flexDirection: "row",
                                justifyContent: "center",
                                alignItems: "center",
                                paddingVertical: 4,
                                borderTopWidth: 1,
                                borderTopColor: theme.colors.lightGrey || theme.colors.grey + "20",
                            }}
                        >
                            <TextM
                                fontSize={10}
                                color={theme.colors.grey}
                                style={{ marginRight: 8 }}
                            >
                                Cette correction t&apos;aide-t-elle ?
                            </TextM>
                            <FeedbackButton
                                onPress={() => handleFeedback('positive')}
                                isSelected={currentFeedback?.feedback_type === 'positive'}
                                isPositive={true}
                                tooltipText="Correction utile"
                                minimal={true}
                                size={{ icon: 14, text: 8 }}
                            />
                            <View style={{ width: 6 }} />
                            <FeedbackButton
                                onPress={() => handleFeedback('negative')}
                                isSelected={currentFeedback?.feedback_type === 'negative'}
                                isPositive={false}
                                tooltipText="Correction peu utile"
                                minimal={true}
                                size={{ icon: 14, text: 8 }}
                            />
                        </View>
                    </View>
                )
            })()}
        </View>
    )
}

const mapStateToProps = (state) => {
    return {
        getMaterialRequest: state.api?.getMaterial?.request,
        getMaterialFeedbackRequest: state.api?.getMaterialFeedback?.request,
        submitMaterialFeedbackRequest: state.api?.submitMaterialFeedback?.request,
        isParentMode: state.data.family?.isParentMode,
    }
}

const mapDispatchToProps = {
    apiRequest,
}

export default connect(mapStateToProps, mapDispatchToProps)(MaterialCorrectionDisplay)